import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { PlayArrow } from '@mui/icons-material';
import EmptyState from '../components/EmptyState';
import { demosService, DemoProjectSummary } from '../services/demos';

const DemosPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const isTr = language === 'tr';
  const [demos, setDemos] = useState<DemoProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    demosService
      .getDemos()
      .then((res) => setDemos(res.demos || []))
      .catch(() => setError(isTr ? 'Demolar yüklenemedi.' : 'Failed to load demos.'))
      .finally(() => setLoading(false));
  }, [language, isTr]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24">
        <div className="flex items-baseline justify-between mb-12">
          <span className="eyebrow"><span className="text-accent">/01</span> · {isTr ? 'DEMOLAR' : 'DEMOS'}</span>
          <span className="eyebrow-mute hidden md:inline">{isTr ? 'ÖRNEK ANALİZLER' : 'SAMPLE WORK'}</span>
        </div>

        <div className="max-w-3xl mb-20">
          <h1 className="display-h display-lg mb-6">
            {isTr ? <>Önce <em>başkasının</em> aynası.</> : <>First, <em>someone else's</em> mirror.</>}
          </h1>
          <p className="font-prose text-[17px] leading-relaxed text-ink-dim max-w-2xl">
            {isTr
              ? 'Mr.Mirror örnek senaryolarda nasıl çalışıyor — gerçek raporlar, gerçek puanlama, gerçek sıralı döküm. Tıkla, içine gir.'
              : 'See Mr.Mirror at work on real scripts — real reports, real scoring, real breakdowns. Click and step inside.'}
          </p>
        </div>

        {loading && (
          <div className="py-16 border-t border-hairline">
            <span className="eyebrow-mute">{isTr ? 'YÜKLENİYOR…' : 'LOADING…'}</span>
          </div>
        )}

        {error && (
          <div className="py-16 border-t border-hairline">
            <span className="eyebrow-mute text-signal-red">/ ERROR</span>
            <p className="font-prose text-[15px] text-ink-dim mt-2">{error}</p>
          </div>
        )}

        {!loading && !error && demos.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-hairline border border-hairline">
            {demos.map((demo, i) => {
              const category = demo.project_type || (isTr ? 'Senaryo' : 'Script');
              const title = demo.file_name?.replace('.pdf', '').replace(/[_-]/g, ' ') || 'Demo';
              const desc = demo.logline
                ? (demo.logline.length > 120 ? demo.logline.slice(0, 120) + '…' : demo.logline)
                : (isTr ? 'Senaryo analizi ve rapor.' : 'Script analysis and report.');
              return (
                <article
                  key={demo.id}
                  onClick={() => navigate(`/demos/${demo.id}`)}
                  className="group relative bg-background cursor-pointer hover:bg-accent-soft transition-colors"
                >
                  <div className="relative w-full aspect-[16/10] bg-elevated overflow-hidden">
                    <img src="/demo-secilen-isler.jpg" alt="" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute inset-0 flex items-center justify-center bg-background/0 group-hover:bg-background/30 transition-colors">
                      <span className="w-14 h-14 border border-accent text-accent flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <PlayArrow sx={{ fontSize: 24 }} />
                      </span>
                    </div>
                  </div>
                  <div className="p-7">
                    <div className="flex items-baseline justify-between mb-4">
                      <span className="eyebrow text-ink-mute">/{String(i + 1).padStart(2, '0')}</span>
                      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-accent">
                        {category.toUpperCase()}
                      </span>
                    </div>
                    <h3 className="font-serif text-2xl text-ink leading-tight mb-3 group-hover:italic group-hover:text-accent transition-all">
                      {title}
                    </h3>
                    <p className="font-prose text-[14px] leading-relaxed text-ink-dim line-clamp-3">
                      {desc}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {!loading && !error && demos.length === 0 && (
          <EmptyState
            title={isTr ? 'Henüz demo eklenmemiş' : 'No demo projects yet'}
            description={isTr
              ? 'Yakında örnek analizler eklenecek. Bu arada kendi senaryonu analiz edebilirsin.'
              : 'Sample analyses will be added soon. In the meantime, you can analyze your own script.'}
            actionLabel={isTr ? 'Analiz Yap' : 'Start Analysis'}
            onAction={() => navigate('/upload')}
          />
        )}

        {/* CTA */}
        <div className="text-center mt-24 pt-16 border-t border-hairline max-w-2xl mx-auto">
          <span className="eyebrow mb-4 block">{isTr ? 'KENDİN İÇİN' : 'FOR YOURSELF'}</span>
          <h2 className="display-h display-md mb-6">
            {isTr ? <>Sıra <em>seninkine</em> geldi.</> : <>Now <em>your turn.</em></>}
          </h2>
          <button onClick={() => navigate('/login')} className="btn-accent">
            {t.startNow}
            <span aria-hidden>→</span>
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DemosPage;
